import { paginationHelper } from '@sparta.global.helpers/pagination-helper/dist/es6.js';
import SpartaGlobalFunctions from '@sparta.global.utilities/sparta-global-functions-utility/dist/es6.js';
import '@sparta.global.utilities/sparta-input-utility/dist/es6.js';

const BASE_CLASS = 'spa-pagination-btn';
const FIRST_CLASS = `${BASE_CLASS}--first`;
const PREV_CLASS = `${BASE_CLASS}--previous`;
const NEXT_CLASS = `${BASE_CLASS}--next`;
const LAST_CLASS = `${BASE_CLASS}--last`;
let buttonInitialized = false;
let paginationTrigger = undefined;

/**
 * Finds active page element.
 * @returns {DOMStringList} active dom element.
 */
function getActivePageElement() {
  return document.activeElement;
}

/**
 * Handles pagination link clicks.
 * @param {object} e - Event arguments.
 * @returns {void}
 */
function pageButtonClickHandler(e) {
  const options = this;
  options.index = parseInt(e.target.dataset.goto, 10);
  options.currentActiveElement = getActivePageElement();
  options.callback(options);
  paginationTrigger = e.target;
}

/**
 * Handles pagination item per page selection.
 * @param {object} e - Event arguments.
 * @returns {void}
 */
function pageSizeSelectDropdown(e) {
  const options = this;
  const selectedIndex = parseInt(e.target.selectedIndex, 10);
  options.size = parseInt(e.target[selectedIndex].value, 10);
  options.index = 0;
  options.currentActiveElement = getActivePageElement();
  options.callback(options);
}

/**
 * Handles pagination item per page list selection.
 * @param {object} e - Event arguments.
 * @returns {void}
 */
function pageListSelectDropdown(e) {
  const options = this;
  const selectedIndex = parseInt(e.target.selectedIndex, 10);
  options.index = parseInt(e.target[selectedIndex].value, 10) - 1;
  options.currentActiveElement = getActivePageElement();
  options.callback(options);
}

/**
 * Attaches required event handler to the utility DOM objects to handle progressive pagination.
 * @param {object} containerData - DOM object.
 * @param {object} options - Utility options.
 * @returns {object} Options object to support function composition.
 */
function paginationBtnBind(containerData, options) {
  containerData.addEventListener('click', pageButtonClickHandler.bind(options));
  buttonInitialized = true;
}

/**
 * Attaches required event handler to the utility DOM objects to handle advanced pagination.
 * @param {object} containerData - DOM object.
 * @param {object} options - Utility options.
 * @returns {object} Options object to support function composition.
 */
function paginationSizeBind(containerData, options) {
  containerData.addEventListener('change', pageSizeSelectDropdown.bind(options));
}

/**
 * Attaches required event handler to the utility DOM objects to handle advanced pagination.
 * @param {object} containerData - DOM object.
 * @param {object} options - Utility options.
 * @returns {object} Options object to support function composition.
 */
function paginationListBind(containerData, options) {
  containerData.addEventListener('change', pageListSelectDropdown.bind(options));
}

/**
 * Sets the focus on the proper pagination navigation button after content load.
 * @param {object} options - Utility options.
 * @returns {void}
 */
function setControlBtnFocus(settings = {}) {
  const {
    options = {}
  } = settings;
  let {
    domContainer
  } = options;
  domContainer = domContainer ? document.getElementById(domContainer.replace('#', '')) : domContainer;
  if (domContainer && paginationTrigger) {
    const nextButton = domContainer.querySelector(`.${NEXT_CLASS}`);
    const previousButton = domContainer.querySelector(`.${PREV_CLASS}`);
    if (paginationTrigger.classList.contains(PREV_CLASS)) {
      if (previousButton.disabled) {
        nextButton.focus();
      } else {
        previousButton.focus();
      }
    } else if (paginationTrigger.classList.contains(NEXT_CLASS)) {
      if (nextButton.disabled) {
        previousButton.focus();
      } else {
        nextButton.focus();
      }
    } else if (paginationTrigger.classList.contains(FIRST_CLASS)) {
      nextButton.focus();
    } else if (paginationTrigger.classList.contains(LAST_CLASS)) {
      previousButton.focus();
    }
    paginationTrigger = undefined;
  }
}
const elementsToBind = [{
  className: 'spa-pagination-btn',
  method: paginationBtnBind
}, {
  className: 'spa-pagination-page-size',
  method: paginationSizeBind
}, {
  className: 'spa-pagination-page-list',
  method: paginationListBind
}];

/**
 * Attaches required event handlers to the utility DOM objects to handle pagination.
 * @param {object} options - Utility options.
 * @param {function} callback - Function to bind event.
 * @param {string} classToBind - Class name.
 * @returns {object} Options object to support function composition.
 */
function attachEventHandlers(options, callback, classToBind) {
  const container = document.querySelector(options.domContainer);
  if (!container) {
    return;
  }
  const pageButtons = container.querySelectorAll(`.${classToBind}`);
  for (let i = 0; i < pageButtons.length; i++) {
    callback(pageButtons[i], options);
  }
}

/**
 * spartaPaginationUtility
 */
class spartaPaginationUtility {
  /**
   * Initializes pagination using supplied options.
   * @param {object} options - Pagination options.
   * @returns {void}
   */
  init(options = {}) {
    if (typeof options.callback === 'function') {
      // DO NOT render pagination if init is invoked from DataTable since
      // DataTable already takes care of rendering pagination using its own helper
      if (!options.fromDataTable && options.domContainer) {
        const lang = SpartaGlobalFunctions.getLanguage();
        const jcrData = SpartaGlobalFunctions.getSpaParams().global[lang].pagination;
        const helperOptions = {
          ...options,
          jcrData
        };
        document.querySelector(options.domContainer).innerHTML = paginationHelper(helperOptions);
        if (buttonInitialized) {
          setControlBtnFocus({
            options
          });
        }
      }
      elementsToBind.forEach(data => {
        attachEventHandlers(options, data.method, data.className);
      });
    }
  }
}

export { spartaPaginationUtility as default };
